import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Quill } from '@/components/Icons.tsx'
import { LeafDivider, Bloom } from '@/components/Botanical.tsx'
import { api, ApiError } from '@/lib/api.ts'
import { useAuth } from '@/state/AuthContext.tsx'
import { initials, timeAgo } from '@/lib/format.ts'
import type { Answer, Member, Prompt } from '@/lib/types.ts'

type Loaded = {
  prompt: Prompt
  released: boolean
  memberCount: number
  answers: Answer[]
  answeredBy: string[]
  members: Member[]
}

export default function TogetherPrompt() {
  const { id = '' } = useParams()
  const { user } = useAuth()
  const [data, setData] = useState<Loaded | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textarea = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    let cancelled = false
    api.prompts
      .one(id)
      .then((d) => { if (!cancelled) setData(d) })
      .catch(() => { if (!cancelled) setError('This one could not be opened.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const text = textarea.current?.value.trim() ?? ''
    if (!text) return
    setSaving(true)
    setError(null)
    try {
      setData(await api.prompts.answer(id, text))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save.')
    } finally {
      setSaving(false)
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') event.currentTarget.form?.requestSubmit()
  }

  if (loading) return <p className="pt-16 text-whisper">Opening…</p>
  if (!data) return <p className="pt-16 text-terracotta">{error ?? 'No such question.'}</p>

  const mine = data.answers.find((a) => a.userId === user?.id)
  const others = data.members.filter((m) => m.id !== user?.id)
  const waitingOn = others.filter((m) => !data.answeredBy.includes(m.id))
  const long = data.prompt.kind === 'long'

  return (
    <>
      <div className="max-w-measure animate-unfurl pb-10 pt-6">
        <Link to="/together" className="eyebrow transition-colors hover:text-sage">← All of them</Link>
        <h1 className={`mt-4 font-display leading-[1.18] ${long ? 'text-[1.9rem]' : 'text-[2.2rem]'}`}>
          {data.prompt.text}
        </h1>
      </div>

      {/* Your own answer, or the space to write one. */}
      {!mine ? (
        <form onSubmit={submit} className="sheet max-w-measure p-5">
          <textarea
            ref={textarea}
            rows={long ? 10 : 4}
            autoFocus
            placeholder={long ? 'Take your time. Ordinary detail is the useful kind.' : 'Whatever comes first is usually the honest one…'}
            onKeyDown={onKeyDown}
            className="w-full resize-none bg-transparent font-body text-[1.02rem] leading-[1.7] text-ink outline-none placeholder:text-whisper"
          />
          <div className="mt-3 flex items-center justify-between gap-4 border-t border-linen pt-3">
            <span className="text-[0.72rem] text-whisper">
              {others.length > 0 ? `${others.map((m) => m.displayName).join(' and ')} won't see this until you've both written` : 'Nobody else can see this yet'}
            </span>
            <button type="submit" disabled={saving} className="btn-primary py-2 text-[0.85rem]">
              <Quill className="h-4 w-4" />
              {saving ? 'Saving…' : 'Done'}
            </button>
          </div>
        </form>
      ) : (
        <div className="max-w-measure space-y-10">
          {data.answers
            .slice()
            .sort((a) => (a.userId === user?.id ? -1 : 1))
            .map((answer) => {
              const author = data.members.find((m) => m.id === answer.userId)
              return (
                <article key={answer.userId} className="animate-unfurl">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[0.55rem] tracking-wider text-paper"
                      style={{ background: author?.accent ?? 'var(--c-whisper)' }}
                    >
                      {initials(author?.displayName ?? '?')}
                    </span>
                    <span className="text-[0.82rem] text-quiet">
                      {answer.userId === user?.id ? 'You' : author?.displayName ?? 'Someone'}
                    </span>
                    <span className="text-[0.72rem] text-whisper">{timeAgo(answer.createdAt)}</span>
                  </div>
                  <p
                    className="mt-2.5 whitespace-pre-wrap border-l-2 pl-4 text-[1.02rem] leading-[1.75] text-ink/90"
                    style={{ borderColor: author?.accent ?? 'var(--c-linen)' }}
                  >
                    {answer.body}
                  </p>
                </article>
              )
            })}

          {!data.released && (
            <div className="relative overflow-hidden rounded-leaf border border-linen bg-parchment/40 px-6 py-10 text-center">
              <Bloom tone="var(--c-clay)" className="pointer-events-none absolute -right-3 -top-3 h-16 w-16 opacity-30 animate-drift" aria-hidden />
              <p className="eyebrow">Sealed for now</p>
              <p className="prose-soft mx-auto mt-3 max-w-[24rem] text-[0.95rem]">
                {data.memberCount < 2
                  ? 'Nothing opens until there are two of you here.'
                  : `${waitingOn.map((m) => m.displayName).join(' and ')} hasn't written one yet. When they do, both answers open at once.`}
              </p>
            </div>
          )}
        </div>
      )}

      {error && <p className="mt-4 max-w-measure text-[0.88rem] text-terracotta">{error}</p>}
      <LeafDivider tone="var(--c-sage)" className="mx-auto mt-20 h-4 w-40 opacity-30" />
    </>
  )
}
